import {FC, useCallback, useState} from 'react';
import {useCan} from 'src/permissions/hooks';
import {IconButton, Stack} from '@mui/material';
import {Delete, HttpsOutlined} from '@mui/icons-material';
import {EditIcon} from '../../../icons/EditIcon';
import {useAppDispatch} from '../../../../app/hooks';
import {tagsApi} from '../../../../api';
import {preloaderChangeStatus} from '../../../../stores/common/commonSlice';
import {UpdateRfidDTO, UpdateRfidRequestForDedicatedPage} from '../../../../types';
import {NO_EMPLOYEE_OPTION} from '../../../forms/formData/rfid/rfid';
import {RfidActionsProps} from './RfidActions.types';
import {rfidActionsSx} from './RfidActions.sx';
import AMPModal from '../../AMPModal';
import {useIntl} from 'react-intl';
import {UpdateRfidForm} from '../../../forms/UpdateRfidForm';
import {BlockStatusEnum} from '../RfidTable.types';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';

export const RfidActions: FC<RfidActionsProps> = ({
                                                      businessId,
                                                      ocppTagId,
                                                      blocked,
                                                      idTag,
                                                      licensePlate,
                                                      tagCompanyId,
                                                      tagEmployeeId,
                                                      tagUserId,
                                                  }) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();

    const [triggerDeleteTag] = tagsApi.useDeleteTagMutation();
    const [triggerToggleTag] = tagsApi.useToggleTagStatusMutation();
    const [triggerUpdateTag] = tagsApi.useUpdateTagMutation();
    const [showUpdateModal, setShowUpdateModal] = useState(false);

    const canUpdateRfid = useCan('rfidTags.update');
    const canDeleteRfid = useCan('rfidTags.delete');

    const handleShowUpdateModal = (status: boolean) => {
        return () => {
            setShowUpdateModal(status);
        };
    };

    const handleRfidDelete = async () => {
        try {
            dispatch(preloaderChangeStatus(true));
            if (businessId && ocppTagId) {
                await triggerDeleteTag({
                    params: {
                        businessId,
                    },
                    tagId: ocppTagId,
                }).unwrap();
            }
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        } finally {
            dispatch(preloaderChangeStatus(false));
        }
    };

    const handleRfidUpdate = async (updatedObject: UpdateRfidDTO) => {
        try {
            dispatch(preloaderChangeStatus(true));
            if (businessId && ocppTagId) {
                const updateData: UpdateRfidRequestForDedicatedPage['data'] = {
                    businessId,
                    blocked: updatedObject.blocked,
                    idTag: updatedObject.idTag,
                    licensePlate: updatedObject.licensePlate ?? null,
                };

                if (tagCompanyId) {
                    // Company tag: keep the company, apply the (possibly changed) employee selection.
                    // employeeId === undefined means the form did not touch it → preserve existing.
                    updateData.companyId = tagCompanyId;
                    const nextEmployeeId =
                        updatedObject.employeeId === undefined ? tagEmployeeId : updatedObject.employeeId;
                    if (nextEmployeeId) {
                        updateData.employeeId = nextEmployeeId;
                    }
                } else if (tagUserId) {
                    // Tag with a private driver
                    updateData.userId = tagUserId;
                }

                await triggerUpdateTag({
                    data: updateData,
                    tagId: ocppTagId,
                }).unwrap();
            }
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        } finally {
            dispatch(preloaderChangeStatus(false));
        }
    };

    const handleBlockClick = useCallback(async () => {
        try {
            dispatch(preloaderChangeStatus(true));
            if (businessId && ocppTagId) {
                await triggerToggleTag({
                    params: {
                        businessId,
                        blocked: !blocked,
                    },
                    tagId: ocppTagId,
                }).unwrap();
            }
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        } finally {
            dispatch(preloaderChangeStatus(false));
        }
    }, [businessId, ocppTagId, blocked, triggerToggleTag, dispatch]);

    const handleDeleteClick = () => {
        void handleRfidDelete();
    };

    return (
        <>
            <Stack sx={rfidActionsSx.root}>
                {canUpdateRfid && (
                    <IconButton
                        sx={rfidActionsSx.button}
                        onClick={handleBlockClick}
                    >
                        <HttpsOutlined sx={rfidActionsSx.icon}/>
                    </IconButton>
                )}

                {canUpdateRfid && (
                    <IconButton
                        sx={rfidActionsSx.button}
                        onClick={handleShowUpdateModal(true)}
                    >
                        <EditIcon/>
                    </IconButton>
                )}

                {canDeleteRfid && (
                    <IconButton
                        sx={rfidActionsSx.button}
                        onClick={handleDeleteClick}
                    >
                        <Delete sx={rfidActionsSx.icon}/>
                    </IconButton>
                )}
            </Stack>
            <AMPModal
                handleClose={handleShowUpdateModal(false)}
                open={showUpdateModal}
                title={intl.formatMessage({id: 'updateRfid'})}
                type="md"
                fullWidth
                withoutPaddings
            >
                <UpdateRfidForm
                    handleClose={handleShowUpdateModal(false)}
                    initialValues={{
                        blocked: blocked ? BlockStatusEnum.blocked : BlockStatusEnum.accepted,
                        idTag,
                        licensePlate: licensePlate ?? '',
                        employeeId: tagEmployeeId ? String(tagEmployeeId) : NO_EMPLOYEE_OPTION,
                    }}
                    companyId={tagCompanyId}
                    businessId={businessId}
                    handleSubmit={handleRfidUpdate}
                />
            </AMPModal>
        </>
    );
};
